import { useAtom } from 'jotai';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Download, Loader2, ChevronDown } from 'lucide-react';
import { AttachmentMetadata } from '@/types/tips';
import {
  attachmentDownloadLoadingAtom,
  downloadAttachmentActionAtom,
} from '@/stores/tipsStore';
import { formatFileSize } from '@/utils/formatFileSize';

interface DownloadAttachmentsDropdownProps {
  attachmentsMetadata?: string | null | undefined;
  tipId?: string;
}

export function DownloadAttachmentsDropdown({
  attachmentsMetadata,
  tipId,
}: DownloadAttachmentsDropdownProps) {
  const [, downloadAttachment] = useAtom(downloadAttachmentActionAtom);
  const [isDownloading] = useAtom(attachmentDownloadLoadingAtom);

  function parseAttachments(): AttachmentMetadata[] {
    if (!attachmentsMetadata) {
      return [];
    }

    try {
      const parsed = JSON.parse(attachmentsMetadata);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  const attachments = parseAttachments();

  async function handleDownload(attachment: AttachmentMetadata) {
    if (!tipId) return;

    await downloadAttachment({
      tipId,
      s3Key: attachment.s3_key,
      filename: attachment.filename,
    });
  }

  const hasAttachments = attachments.length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='outline'
          size='sm'
          className='gap-2'
          disabled={!hasAttachments || !tipId}
        >
          <Download className='h-4 w-4' />
          {hasAttachments
            ? `Download attachments (${attachments.length})`
            : 'No attachments'}
          <ChevronDown className='h-3 w-3 opacity-50' />
        </Button>
      </DropdownMenuTrigger>
      {hasAttachments && (
        <DropdownMenuContent align='end' className='w-80'>
          <DropdownMenuGroup>
            {attachments.map(attachment => (
              <DropdownMenuItem
                key={attachment.s3_key}
                className='flex items-center justify-between p-3 cursor-pointer'
                onClick={() => handleDownload(attachment)}
                disabled={isDownloading === attachment.s3_key || !tipId}
              >
                <div className='flex items-center gap-3 flex-1 min-w-0'>
                  <div className='flex-1 min-w-0'>
                    <div className='text-sm font-medium truncate'>
                      {attachment.filename}
                    </div>
                    <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                      <span>{formatFileSize(attachment.size)}</span>
                    </div>
                  </div>
                </div>
                {isDownloading === attachment.s3_key ? (
                  <Loader2 className='h-6 w-6 animate-spin flex-shrink-0 text-muted-foreground' />
                ) : (
                  <Download className='h-6 w-6 flex-shrink-0 text-muted-foreground' />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
}
